import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiCheck, FiSearch, FiRotateCw, FiMaximize2 } from 'react-icons/fi';
import { API_URL } from '../config';

/**
 * Choose GSWCC construction details to attach to a BMP code.
 *
 * The manual does not map figures to practices unambiguously — some practices
 * cite each other's figure sections — so nothing here is guessed. Every detail
 * page from the manual is shown as a thumbnail and picked by eye, once.
 *
 * Thumbnails are fetched as blobs because the endpoint needs a bearer token an
 * <img> would not send.
 */
function BmpFigurePicker({ symbol, onSave, onClose }) {
  const [figures, setFigures] = useState([]);
  // [{ key, rotate }] — some GSWCC details are drawn sideways on a portrait
  // page, and which ones is not reliably detectable, so rotation is chosen here.
  const [selected, setSelected] = useState(
    (symbol.figure_keys || []).map(f => (typeof f === 'string' ? { key: f, rotate: 0 } : f)),
  );
  const [thumbs, setThumbs] = useState({});
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // The drawing being previewed full size. Thumbnails are too small to tell
  // one silt-fence detail from another, which is the whole difficulty here.
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const objectUrls = useRef([]);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/bmp-figures`, config);
        setFigures(res.data || []);
      } catch (err) {
        console.error('Could not load figure catalogue', err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadThumb = useCallback(async (key) => {
    try {
      const res = await axios.get(`${API_URL}/bmp-figures/${key}/image?thumb=1`,
        { ...config, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      objectUrls.current.push(url);
      setThumbs(prev => ({ ...prev, [key]: url }));
    } catch (err) {
      /* a missing thumbnail is cosmetic */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    figures.forEach(f => { if (!thumbs[f.key]) loadThumb(f.key); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figures]);

  useEffect(() => () => {
    objectUrls.current.forEach(URL.revokeObjectURL);
    objectUrls.current = [];
  }, []);

  const openPreview = async (fig, e) => {
    e.stopPropagation();
    setPreview(fig);
    setPreviewUrl(null);
    try {
      const res = await axios.get(`${API_URL}/bmp-figures/${fig.key}/image`,
        { ...config, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      objectUrls.current.push(url);
      setPreviewUrl(url);
    } catch (err) {
      alert('Could not load that drawing: ' + (err.response?.data?.error || err.message));
      setPreview(null);
    }
  };

  const indexOf = (key) => selected.findIndex(s => s.key === key);

  const toggle = (fig) => {
    const i = indexOf(fig.key);
    if (i >= 0) {
      setSelected(prev => prev.filter(s => s.key !== fig.key));
    } else if (selected.length < 6) {
      // Start from the manual's own orientation where we could measure it.
      setSelected(prev => [...prev, { key: fig.key, rotate: fig.rotate || 0 }]);
    }
  };

  // Rotating an unpicked drawing picks it: turning it upright is itself a
  // decision that you want it, and hiding the control until after selection is
  // how it went unnoticed the first time.
  const rotate = (fig, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const i = prev.findIndex(x => x.key === fig.key);
      if (i >= 0) {
        return prev.map(x => (x.key === fig.key
          ? { ...x, rotate: ((x.rotate || 0) + 90) % 360 } : x));
      }
      if (prev.length >= 6) return prev;
      return [...prev, { key: fig.key, rotate: ((fig.rotate || 0) + 90) % 360 }];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/bmp-symbols/${symbol.id}`,
        { figure_keys: selected }, config);
      onSave();
    } catch (err) {
      alert('Could not save: ' + (err.response?.data?.error || err.message));
      setSaving(false);
    }
  };

  const ctlStyle = {
    background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px',
    width: '26px', height: '26px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', color: '#334155',
    boxShadow: '0 1px 3px rgba(0,0,0,.12)',
  };

  const q = query.trim().toLowerCase();
  const shown = q
    ? figures.filter(f => (`${f.figure} ${f.title || ''} p${f.page}`).toLowerCase().includes(q))
    : figures;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 3200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
    }}>
      <div style={{
        background: 'white', borderRadius: '12px', width: '100%', maxWidth: '940px',
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 18px 50px rgba(0,0,0,.4)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
        }}>
          <div>
            <strong style={{ fontSize: '15px', color: '#111827' }}>
              Construction details for {symbol.code}
            </strong>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {symbol.description || 'Pick the drawings that apply'} &middot; {selected.length} of 6 selected
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', lineHeight: 0 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ padding: '12px 18px 0' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by figure number, title or manual page..."
              style={{
                width: '100%', padding: '9px 11px 9px 32px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0' }}>
            From the GSWCC <em>Manual for Erosion and Sediment Control in Georgia</em>, 2016 Edition.
            Whatever you pick prints on the BMP details page of any letter using {symbol.code}.
            Use <FiMaximize2 size={11} style={{ verticalAlign: '-1px' }} /> to view a drawing
            full size, and <FiRotateCw size={11} style={{ verticalAlign: '-1px' }} /> to turn one
            that the manual prints sideways.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {loading ? (
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>Loading catalogue...</div>
          ) : (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px',
            }}>
              {shown.map(f => {
                const i = indexOf(f.key);
                const on = i >= 0;
                const deg = on ? (selected[i].rotate || 0) : (f.rotate || 0);
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => toggle(f)}
                    style={{
                      border: on ? '2px solid #0f2942' : '1px solid #e5e7eb',
                      borderRadius: '9px', padding: '8px', background: on ? '#f2f6fb' : 'white',
                      cursor: 'pointer', textAlign: 'left', position: 'relative',
                    }}
                  >
                    {on && (
                      <span style={{
                        position: 'absolute', right: '8px', top: '8px', background: '#0f2942',
                        color: 'white', borderRadius: '999px', width: '22px', height: '22px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <FiCheck size={13} />
                      </span>
                    )}
                    {/* Always present, selected or not, and labelled. */}
                    <span style={{ position: 'absolute', left: '8px', top: '8px', display: 'flex', gap: '5px' }}>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => openPreview(f, e)}
                        onKeyDown={(e) => { if (e.key === 'Enter') openPreview(f, e); }}
                        title="View this drawing full size"
                        style={ctlStyle}
                      >
                        <FiMaximize2 size={12} />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => rotate(f, e)}
                        onKeyDown={(e) => { if (e.key === 'Enter') rotate(f, e); }}
                        title="Rotate 90 degrees"
                        style={ctlStyle}
                      >
                        <FiRotateCw size={12} />
                      </span>
                    </span>
                    <div style={{
                      height: '250px', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', overflow: 'hidden', background: 'white',
                    }}>
                      {thumbs[f.key]
                        ? (
                          <img
                            src={thumbs[f.key]}
                            alt=""
                            style={{
                              maxWidth: deg % 180 ? '150px' : '100%',
                              maxHeight: deg % 180 ? '100%' : '100%',
                              transform: deg ? `rotate(${deg}deg)` : undefined,
                            }}
                          />
                        )
                        : <span style={{ fontSize: '11px', color: '#d1d5db' }}>loading</span>}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827', marginTop: '6px' }}>
                      {f.kind === 'table' ? 'Table' : 'Figure'} {f.figure}
                      {on && deg ? <span style={{ color: '#6b7280', fontWeight: 400 }}> &middot; {deg}&deg;</span> : null}
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: 1.35 }}>
                      {f.title || `Manual page ${f.page}`}
                    </div>
                    {f.own_caption === false && (
                      <div style={{ fontSize: '10.5px', color: '#b45309', marginTop: '2px' }}>
                        page cites this number
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {!loading && !shown.length && (
            <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
              No figures match &ldquo;{query}&rdquo;.
            </div>
          )}
        </div>

        {preview && (
          <div
            onClick={() => { setPreview(null); setPreviewUrl(null); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,.82)', zIndex: 3400,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '20px', cursor: 'zoom-out',
            }}
          >
            <div style={{ color: 'white', fontSize: '14px', marginBottom: '10px', textAlign: 'center' }}>
              <strong>{preview.kind === 'table' ? 'Table' : 'Figure'} {preview.figure}</strong>
              {preview.title ? ` — ${preview.title}` : ''}
              <span style={{ opacity: .7 }}> · manual page {preview.page}</span>
            </div>
            {previewUrl
              ? (
                <img
                  src={previewUrl}
                  alt=""
                  style={{
                    maxWidth: '96vw', maxHeight: '80vh', background: 'white',
                    transform: (() => {
                      const i = indexOf(preview.key);
                      const deg = i >= 0 ? (selected[i].rotate || 0) : (preview.rotate || 0);
                      return deg ? `rotate(${deg}deg)` : undefined;
                    })(),
                  }}
                />
              )
              : <span style={{ color: 'white', opacity: .8 }}>Loading full size...</span>}
            <div style={{ color: 'white', opacity: .65, fontSize: '12px', marginTop: '12px' }}>
              Click anywhere to close
            </div>
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          padding: '12px 18px', borderTop: '1px solid #e5e7eb',
        }}>
          <button onClick={onClose}
            style={{ padding: '10px 16px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            style={{ padding: '10px 18px', background: '#0f2942', color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
            {saving ? 'Saving...' : `Attach ${selected.length} detail${selected.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BmpFigurePicker;
